import { config } from "../config";
import {
  TSession,
  TNoteType,
  TAudioType,
  TAudioFormat,
  TEncounterType,
  RecordingUploadMode,
  RemoteUploadSessionStatus,
  TSummaryTemplateData,
  TSummaryTemplateResponse,
  TSummaryTemplateDefinition,
  SummaryPromptV1,
} from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { mixpanel } from "../utils/mixpanel";
import { logUIError } from "./api-misc";

// ------------------------------
// Config + Endpoint Setup
// ------------------------------

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefixDocumentation = `${protocol}://${host}/clinics/v1/documentation`;

const logDocumentationUIError = async (
  message: string,
  extraContext?: Record<string, unknown>,
) => {
  try {
    await logUIError({
      message,
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      context: {
        component: "api-documentation",
        ...extraContext,
      },
    });
  } catch (err) {
    console.warn("logDocumentationUIError failed", err);
  }
};

// ------------------------------
// Types
// ------------------------------

export type PreSignedURLResponse = {
  audio_id: number;
  presigned_url: {
    put_url: string;
    get_url: string;
  };
};

export type ChunkedCreateAudioResponse = {
  audio_id: number;
  upload_session_id: string;
  upload_mode: "chunked_resumable";
  segment_duration_seconds: number;
  recommended_presign_batch_size: number;
  next_part_number: number;
  status_get_url: string;
};

export type BackendTranscribeMetadata = {
  success: boolean;
  transcript_s3_key?: string;
  summary?: string;
  error?: string;
};

export type BackendTranscribeResponse = {
  summary: string;
  transcript_s3_key: string;
  transcription: string;
};

export type ProcessedAudioResponse = {
  summary: string;
  transcript_s3_key: string;
  transcription: string;
  audioUrl: string;
  audioId: number;
};

// ------------------------------
// Helpers
// ------------------------------

export const uploadAudio = async (
  audioBlob: Blob,
  presignedUrl: string,
  context: {
    session?: TSession;
    patientId?: string;
    audioId?: number;
    source?: string;
  } = {},
): Promise<void> => {
  const getSafeAudioContentType = (blob: Blob): string => {
    const allowedTypes = [
      "audio/mpeg",
      "audio/mp3",
      "audio/mp4",
      "audio/m4a",
      "audio/webm",
      "audio/wav",
      "audio/ogg",
    ];
    if (blob.type && allowedTypes.includes(blob.type)) return blob.type;

    console.warn(
      "⚠️ Unknown or missing audio type. Falling back to 'application/octet-stream'",
      {
        actualType: blob.type,
      },
    );

    return "application/octet-stream";
  };

  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: audioBlob,
    headers: {
      "Content-Type": getSafeAudioContentType(audioBlob),
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    const requestId =
      response.headers.get("x-amz-request-id") ||
      response.headers.get("x-amz-id-2") ||
      undefined;

    const failureContext = {
      status: response.status,
      statusText: response.statusText,
      requestId,
      responseBody: errorText.slice(0, 1000),
      audioType: audioBlob.type || "unknown",
      audioSize: audioBlob.size,
      patientId: context.patientId,
      audioId: context.audioId,
      clinicId: context.session?.clinicId,
      source: context.source || "unknown-source",
    };

    console.error("[AudioUpload] PUT failed", failureContext);

    const warningProps: Record<string, unknown> = {
      "User Email": context.session?.userEmail,
      "Clinic Id": context.session?.clinicId,
      "Clinic Name": context.session?.clinicName,
      "Patient ID": context.patientId,
      "Audio ID": context.audioId,
      "Warning Type": "Presigned PUT Failed",
      "HTTP Status": response.status,
      "HTTP Status Text": response.statusText,
      "S3 Request ID": requestId,
      Source: context.source || "unknown-source",
      "Audio MIME Type": audioBlob.type || "unknown",
      "Audio Size Bytes": audioBlob.size,
    };

    mixpanel.track("Voice Note Upload Warning", warningProps);

    try {
      await logUIError({
        message: "Voice note upload PUT failed",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: context.session?.userEmail || undefined,
        clinic_name: context.session?.clinicName || undefined,
        clinic_id: context.session?.clinicId || undefined,
        context: failureContext,
      });
    } catch (err) {
      console.warn("logUIError for upload failure failed", err);
    }

    throw new Error(
      `Audio upload PUT failed: ${response.status} ${response.statusText}${requestId ? ` (requestId=${requestId})` : ""}`,
    );
  }
};

// ------------------------------
// Audio
// ------------------------------

type AudioUploadLogContext = {
  source?: string;
};

const formatAudioUploadSource = (source?: string) =>
  source ? source : "unknown-source";

type CreatePatientAudioOptions = {
  uploadMode?: RecordingUploadMode;
  segmentDurationSeconds?: number;
  audioFormat?: TAudioFormat;
};

const createPatientAudio = async (
  session: TSession,
  patientId: string,
  audioType: TAudioType = "surgeon_audio",
  dateOfRecording?: string | null,
  createOptions?: CreatePatientAudioOptions,
  logContext: AudioUploadLogContext = {},
): Promise<PreSignedURLResponse | ChunkedCreateAudioResponse> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/patients/${patientId}/audio`;

  const body: Record<string, any> = {
    audio_type: audioType,
    upload_mode: createOptions?.uploadMode || "single_blob",
  };

  if (
    createOptions?.uploadMode === "chunked_resumable" &&
    createOptions.segmentDurationSeconds
  ) {
    body.segment_duration_seconds = createOptions.segmentDurationSeconds;
    // core-api currently validates chunked uploads to webm|m4a only.
    body.audio_format = createOptions.audioFormat || "webm";
  }

  if (dateOfRecording) {
    body.date_of_recording = dateOfRecording;
  }

  const sourceLabel = formatAudioUploadSource(logContext.source);

  console.log(
    `[AudioUpload] Requesting presigned URL from ${url} (patientId=${patientId}) [source=${sourceLabel}]`,
  );

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    console.error(
      `[AudioUpload] Presigned URL request failed with status ${response.status} [source=${sourceLabel}]`,
    );
    throw new Error(`Failed to get presigned URL: ${response.status}`);
  }

  const payload = await response.json();

  console.log(
    `[AudioUpload] Received presigned URL for audio_id=${payload?.audio_id} [source=${sourceLabel}]`,
  );

  return payload;
};

export type TAudioMetadata = {
  id: number;
  s3_key: string;
  type: TAudioType;
  is_uploaded: boolean;
  upload_mode?: RecordingUploadMode;
  upload_status?: string;
  created_at: string;
  date_of_recording?: string | null;
  get_url: string;
  transcript_get_url?: string | null;
};

export type TAudioResponse = {
  metadata: TAudioMetadata[];
};

export const getPatientAudio = async (
  session: TSession,
  clinicId: string,
  patientId: string,
  neverExpire: boolean = false,
  options: { signal?: AbortSignal } = {},
): Promise<TAudioResponse> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/audio`;

  const params = new URLSearchParams();
  if (neverExpire) {
    params.append("never_expire", "true");
  }

  const finalUrl = params.toString() ? `${url}?${params}` : url;

  const response = await fetchWithAuthRetry(finalUrl, {
    method: "GET",
    signal: options.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    logDocumentationUIError("Get patient audio failed", {
      status: response.status,
      clinicId,
      patientId,
      errorText,
      neverExpire,
    });
    throw new Error(
      `Failed to get patient audio: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};
// ------------------------------
// Core: Transcription + Summary
// ------------------------------

export const transcribeAndSummarizeAudio = async (
  session: TSession,
  audioId: number,
  patientId: string,
  encounterType: TEncounterType,
  appointmentId?: number | null,
  isDummyData?: boolean,
  noteDateOverride?: string | null,
  recoverIncompleteChunkedUpload: boolean = false,
): Promise<BackendTranscribeResponse> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/transcribe-and-summarize`;

  const body = {
    patient_uuid: patientId,
    encounter_type: encounterType,
    generate_summary: true,
    is_dummy_data: isDummyData ?? false,
    appointment_id: appointmentId,
    note_date_override: noteDateOverride || undefined,
    recover_incomplete_chunked_upload: recoverIncompleteChunkedUpload,
  };

  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.warn("❌ Transcription start failed:", response.status, errorText);
    logDocumentationUIError("Transcription start failed", {
      status: response.status,
      clinicId: session.clinicId,
      audioId,
      patientId,
      encounterType,
      appointmentId,
      errorText,
    });
    let errorMessage = `Failed to start transcription: ${response.status}`;
    try {
      const errorPayload = JSON.parse(errorText);
      if (typeof errorPayload?.message === "string") {
        errorMessage = errorPayload.message;
      }
    } catch {
      // Keep the status-based fallback for non-JSON responses.
    }
    throw new Error(errorMessage);
  }

  const {
    metadata_get_url,
    transcript_get_url,
  }: {
    metadata_get_url: string;
    transcript_get_url: string;
  } = await response.json();

  const pollInterval = 7500; // 7.5 seconds
  const maxAttempts = 240; // 30 minutes

  let metadata: BackendTranscribeMetadata | null = null;
  try {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const pollResponse = await fetch(metadata_get_url, { method: "GET" });
      if (pollResponse.ok) {
        const polledMetadata: BackendTranscribeMetadata =
          await pollResponse.json();
        metadata = polledMetadata;

        if (polledMetadata.error) {
          break;
        }

        // Treat success=true as ready even if summary is empty/missing.
        if (polledMetadata.success) {
          break;
        }
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    }
  } catch (e) {
    console.warn("⏱️ Polling timeout:", e);
    logDocumentationUIError("Transcription polling timed out", {
      clinicId: session.clinicId,
      audioId,
      patientId,
      encounterType,
      appointmentId,
      pollInterval,
      maxAttempts,
      error: e instanceof Error ? e.message : String(e),
    });
    throw new Error(
      "This audio file is taking a while to process. Come back to this page in a few minutes to see the results.",
    );
  }

  if (!metadata) {
    throw new Error("Missing transcription metadata.");
  }

  if (metadata.error) {
    console.error("❌ Transcription failed:", metadata.error);
    logDocumentationUIError("Transcription failed", {
      clinicId: session.clinicId,
      audioId,
      patientId,
      encounterType,
      appointmentId,
      metadataError: metadata.error,
    });
    throw new Error(`Transcription failed: ${metadata.error}`);
  }

  if (!metadata.success) {
    throw new Error("Missing transcription result in metadata.");
  }

  let transcription = "";
  const transcriptRes = await fetch(transcript_get_url);
  if (!transcriptRes.ok) {
    logDocumentationUIError("Transcript fetch failed", {
      clinicId: session.clinicId,
      audioId,
      patientId,
      encounterType,
      appointmentId,
      status: transcriptRes.status,
    });
  } else {
    transcription = await transcriptRes.text();
  }

  return {
    summary: metadata.summary || "",
    transcript_s3_key: metadata.transcript_s3_key ?? transcript_get_url,
    transcription,
  };
};

// ------------------------------
// Full Pipeline: Upload + Transcribe
// ------------------------------

type BeginAudioUploadOptions = {
  source?: string;
};

export const beginPatientAudioUpload = async (
  session: TSession,
  patientId: string,
  audioType: TAudioType = "surgeon_audio",
  dateOfRecording?: string | null,
  options?: BeginAudioUploadOptions,
) => {
  const sourceLabel = formatAudioUploadSource(options?.source);
  console.log(
    `[AudioUpload] beginPatientAudioUpload called for patientId=${patientId} [source=${sourceLabel}]`,
  );

  const payload = await createPatientAudio(
    session,
    patientId,
    audioType,
    dateOfRecording,
    { uploadMode: "single_blob" },
    { source: options?.source },
  );

  if (!("presigned_url" in payload)) {
    throw new Error("Expected single-blob upload response from API");
  }

  const { audio_id, presigned_url } = payload;

  console.log(
    `[AudioUpload] beginPatientAudioUpload received audioId=${audio_id} [source=${sourceLabel}]`,
  );

  return {
    audioId: audio_id,
    putUrl: presigned_url.put_url,
    getUrl: presigned_url.get_url,
  };
};

export const startAudioProcessing = async (
  session: TSession,
  audioId: number,
  patientId: string,
  encounterType: TEncounterType,
  appointmentId?: number | null,
  noteDateOverride?: string | null,
  recoverIncompleteChunkedUpload: boolean = false,
) => {
  const r = await transcribeAndSummarizeAudio(
    session,
    audioId,
    patientId,
    encounterType,
    appointmentId,
    undefined,
    noteDateOverride,
    recoverIncompleteChunkedUpload,
  );
  return {
    summary: r.summary,
    transcription: r.transcription,
    transcript_s3_key: r.transcript_s3_key,
  };
};

export type BeginPatientChunkedAudioUploadSessionResponse = {
  audioId: number;
  uploadSessionId: string;
  uploadMode: "chunked_resumable";
  segmentDurationSeconds: number;
  recommendedPresignBatchSize: number;
  nextPartNumber: number;
  statusGetUrl: string;
};

export const beginPatientChunkedAudioUploadSession = async (
  session: TSession,
  patientId: string,
  audioType: TAudioType = "surgeon_audio",
  dateOfRecording?: string | null,
  segmentDurationSeconds: number = 30,
  audioFormat: TAudioFormat = "webm",
): Promise<BeginPatientChunkedAudioUploadSessionResponse> => {
  const payload = await createPatientAudio(
    session,
    patientId,
    audioType,
    dateOfRecording,
    {
      uploadMode: "chunked_resumable",
      segmentDurationSeconds,
      audioFormat,
    },
    { source: "api-documentation.ts:beginPatientChunkedAudioUploadSession" },
  );

  if (
    !("upload_mode" in payload) ||
    payload.upload_mode !== "chunked_resumable"
  ) {
    throw new Error("Chunked upload session was not returned by API");
  }

  return {
    audioId: payload.audio_id,
    uploadSessionId: payload.upload_session_id,
    uploadMode: payload.upload_mode,
    segmentDurationSeconds: payload.segment_duration_seconds,
    recommendedPresignBatchSize: payload.recommended_presign_batch_size,
    nextPartNumber: payload.next_part_number,
    statusGetUrl: payload.status_get_url,
  };
};

export type PresignAudioPartsRequest = {
  upload_session_id: string;
  start_part_number?: number;
  count?: number;
  part_numbers?: number[];
};

export type PresignAudioPartsResponse = {
  parts: {
    part_number: number;
    put_url: string;
    expires_at?: string;
    s3_key?: string;
  }[];
};

export const presignAudioParts = async (
  session: TSession,
  audioId: number,
  request: PresignAudioPartsRequest,
): Promise<PresignAudioPartsResponse> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/parts/presign`;
  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to presign audio parts: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

export type CompleteAudioPartRequest = {
  upload_session_id: string;
  part_number: number;
  etag: string;
  size_bytes: number;
  sha256?: string;
};

export const completeAudioPart = async (
  session: TSession,
  audioId: number,
  request: CompleteAudioPartRequest,
): Promise<void> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/parts/complete`;
  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to complete audio part: ${response.status} ${errorText}`,
    );
  }
};

export const getAudioUploadStatus = async (
  session: TSession,
  audioId: number,
  uploadSessionId: string,
): Promise<RemoteUploadSessionStatus> => {
  const params = new URLSearchParams({
    upload_session_id: uploadSessionId,
  });
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/upload-status?${params.toString()}`;
  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to get upload status: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

export type FinalizeAudioUploadRequest = {
  upload_session_id: string;
  final_part_number: number;
};

export type FinalizeAudioUploadResponse = {
  status: string;
  status_get_url?: string;
  audio_id?: number;
};

export const finalizeAudioUpload = async (
  session: TSession,
  audioId: number,
  request: FinalizeAudioUploadRequest,
): Promise<FinalizeAudioUploadResponse> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/finalize`;
  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to finalize upload: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

export type AbortAudioUploadRequest = {
  upload_session_id: string;
};

export const abortAudioUpload = async (
  session: TSession,
  audioId: number,
  request: AbortAudioUploadRequest,
): Promise<void> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/audio/${audioId}/abort-upload`;
  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to abort upload: ${response.status} ${errorText}`);
  }
};

export type ReprocessAudioAsDifferentNoteTypeParams = {
  session: TSession;
  audioId: number;
  patientId: string;
  encounterType: TEncounterType;
  appointmentId?: number | null;
  noteDateOverride?: string | null;
  recoverIncompleteChunkedUpload?: boolean;
  startedAtMs?: number;
  maxAttempts?: number;
  pollDelayMs?: number;
  onProgress?: (
    stage: "processing_started" | "polling_notes",
    attempt?: number,
  ) => void;
};

export type ReprocessAudioAsDifferentNoteTypeResult = {
  status: "completed" | "still_processing";
  matchedNoteId?: number;
};

export const reprocessAudioAsDifferentNoteType = async (
  params: ReprocessAudioAsDifferentNoteTypeParams,
): Promise<ReprocessAudioAsDifferentNoteTypeResult> => {
  const {
    session,
    audioId,
    patientId,
    encounterType,
    appointmentId,
    noteDateOverride,
    recoverIncompleteChunkedUpload = false,
    startedAtMs = Date.now(),
    maxAttempts = 180,
    pollDelayMs = 5000,
    onProgress,
  } = params;
  if (!session?.clinicId) {
    throw new Error("Missing clinic ID for note reprocessing.");
  }

  onProgress?.("processing_started");
  await startAudioProcessing(
    session,
    audioId,
    patientId,
    encounterType,
    appointmentId,
    noteDateOverride,
    recoverIncompleteChunkedUpload,
  );

  const normalizeType = (value: string | null | undefined) =>
    (value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
  const targetType = normalizeType(encounterType);

  onProgress?.("polling_notes", 0);
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const notes = await getPatientNotes(session.clinicId, patientId);

    const matched = notes.find((note) => {
      const createdAtMs = new Date(note.created_at).getTime();
      const encounterTypeRaw =
        note.encounter_type ?? note.audio_info?.[0]?.encounter_type ?? "";
      const hasMatchingAudio = (note.audio_info || []).some(
        (audio) => audio.id === audioId,
      );

      return (
        hasMatchingAudio &&
        normalizeType(encounterTypeRaw) === targetType &&
        createdAtMs >= startedAtMs - 1000
      );
    });

    if (matched) {
      return {
        status: "completed",
        matchedNoteId: matched.id,
      };
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, pollDelayMs));
      onProgress?.("polling_notes", attempt + 1);
    }
  }

  return { status: "still_processing" };
};

export const processAudioRecording = async (
  session: TSession,
  audioBlob: Blob,
  patientId: string,
  encounterType: TEncounterType,
  appointmentId?: number | null,
  noteDateOverride?: string | null,
): Promise<ProcessedAudioResponse> => {
  const { audioId, putUrl, getUrl } = await beginPatientAudioUpload(
    session,
    patientId,
    "surgeon_audio",
    noteDateOverride || undefined,
    { source: "api-documentation.ts:processAudioRecording" },
  );
  await uploadAudio(audioBlob, putUrl, {
    session,
    patientId,
    audioId,
    source: "api-documentation.ts:processAudioRecording",
  });
  const r = await startAudioProcessing(
    session,
    audioId,
    patientId,
    encounterType,
    appointmentId,
    noteDateOverride,
  );
  return { ...r, audioUrl: getUrl, audioId };
};

// ------------------------------
// AI Note Generation Function
// ------------------------------

export type CreateAiNoteRequest = {
  audio_id?: number;
  text?: string;
  patient_uuid?: string;
  is_dummy_data?: boolean;
  appointment_id?: number | null;
  note_date_override?: string | null;
};

export type CreateAiNoteResponse = {
  note_id: number;
  audio_ids: number[];
  note?: string;
  note_json?: Record<string, any>;
  processing_metadata?: Record<string, any>;
};

export const createAiNote = async (
  session: TSession,
  request: CreateAiNoteRequest,
): Promise<CreateAiNoteResponse> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/generate-ai-note`;

  const body: Record<string, any> = {
    audio_id: request.audio_id,
    text: request.text,
    patient_uuid: request.patient_uuid,
    is_dummy_data: request.is_dummy_data || false,
  };

  if (request.appointment_id !== undefined) {
    body.appointment_id = request.appointment_id;
  }

  if (request.note_date_override) {
    body.note_date_override = request.note_date_override;
  }

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.warn("❌ AI note generation failed:", response.status, errorText);
    logDocumentationUIError("AI note generation failed", {
      status: response.status,
      clinicId: session.clinicId,
      patientId: request.patient_uuid,
      appointmentId: request.appointment_id,
      errorText,
    });
    throw new Error(`Failed to generate AI note: ${response.status}`);
  }

  return response.json();
};

// ------------------------------
// SOAP Template Management
// ------------------------------

export type GetTemplateResponseSchema = {
  template_data: Record<string, any>;
  template_source: string;
};

export type AdjustSoapTemplateRequestSchema = {
  template_data: Record<string, any>;
  integration_type?: string;
};

export type AdjustSoapTemplateResponseSchema = {
  template_data: Record<string, any> | null;
  template_source: string | null;
};

/**
 * Get SOAP template for a clinic
 * GET /{clinic_id}/soap-template
 */
export const getSoapTemplate = async (
  session: TSession,
): Promise<GetTemplateResponseSchema> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/soap-template`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to get SOAP template: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

/**
 * Create or update SOAP template for a clinic
 * PATCH /{clinic_id}/soap-template
 */
export const createOrUpdateSoapTemplate = async (
  session: TSession,
  request: AdjustSoapTemplateRequestSchema,
): Promise<AdjustSoapTemplateResponseSchema> => {
  const url = `${urlPrefixDocumentation}/${session.clinicId}/soap-template`;

  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update SOAP template: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

// ------------------------------
// Note Management
// ------------------------------

export type TUpdateNoteRequest = {
  note: string;
  note_json?: any;
  audio_ids?: number[];
};

export type TPatientNote = {
  id: number;
  note: string;
  note_json?: any;
  created_at: string;
  audio_ids?: number[];
};

export const updatePatientNote = async (
  session: TSession,
  clinicId: string,
  patientId: string,
  noteId: number,
  payload: TUpdateNoteRequest,
): Promise<TPatientNote> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/notes/${noteId}`;

  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update note: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const deletePatientNote = async (
  clinicId: string,
  patientId: string,
  noteId: number,
): Promise<void> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/notes/${noteId}`;

  const response = await fetchWithAuthRetry(url, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete note: ${response.status} - ${errorText}`);
  }
};

export type TAudioInfo = {
  id: number;
  s3_key: string | null;
  get_url: string | null;
  type: string;
  encounter_type?: string | null;
  duration_in_seconds?: number | null;
  transcription_s3_key?: string | null;
  transcription_get_url?: string | null;
};

export type TPatientNoteWithAudio = {
  id: number;
  note: string;
  note_json?: any;
  note_type: TNoteType;
  encounter_type: string | null;
  created_at: string;
  audio_ids?: number[];
  audio_info?: TAudioInfo[];
  is_signed?: boolean;
  signed_at?: string | null;
  signed_pdf_url?: string | null;
};

export type TSignPatientNoteResponse = {
  id: number;
  note: string;
  note_json?: any;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  date_of_recording?: string | null;
  audio_ids?: number[];
  is_signed?: boolean;
  signed_at?: string | null;
  signed_pdf_url?: string | null;
};

export const getPatientNotes = async (
  clinicId: string,
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<TPatientNoteWithAudio[]> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/notes`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
    signal: options.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch notes: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const signPatientNote = async (
  clinicId: string,
  patientId: string,
  noteId: number,
  options: {
    signedAtOverride?: string | null;
  } = {},
): Promise<TSignPatientNoteResponse> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/notes/${noteId}/sign`;
  const body =
    options.signedAtOverride && options.signedAtOverride.trim()
      ? { signed_at_override: options.signedAtOverride.trim() }
      : undefined;

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to sign note: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const syncNoteWithIntegration = async (
  session: TSession,
  clinicId: string,
  patientId: string,
  noteId: number,
): Promise<void> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patients/${patientId}/notes/${noteId}/integration-sync`;

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
  });

  if (!response.ok) {
    let errorText;
    try {
      errorText = await response.text();
    } catch {
      errorText = "Unable to read response body";
    }
    throw new Error(`Failed to sync note: ${response.status} - ${errorText}`);
  }
};

// ------------------------------
// Summary Templates
// ------------------------------
// Place near other local types in api-documentation.ts
type SummaryTemplateApiResponse = {
  template_id?: number | null;
  template_data: SummaryPromptV1 | null; // null on default path
  template_source: string | null; // "summary" | "default" | ...
  encounter_type: string | null;
  user_prompt?: string | null; // always provided by backend now
  template_json_version?: string | null; // "summary_prompt/v1" | "system+user/v0" | null
};

const mapSummaryTemplateResponse = (
  payload: SummaryTemplateApiResponse,
): TSummaryTemplateResponse => ({
  templateData: payload.template_data ?? null,
  templateSource: payload.template_source ?? null,
  encounterType: payload.encounter_type ?? null,
  templateId: payload.template_id ?? null,
  userPrompt: payload.user_prompt ?? null,
  templateJsonVersion: payload.template_json_version ?? null,
});

export const getSummaryTemplate = async (
  session: TSession,
  options: { encounterType?: string } = {},
): Promise<TSummaryTemplateResponse> => {
  const url = (() => {
    if (!session?.clinicId) {
      throw new Error(
        "Clinic session is required to manage summary templates.",
      );
    }
    const base = `${urlPrefixDocumentation}/${session.clinicId}/summary-template`;
    if (!options.encounterType) return base;
    const sp = new URLSearchParams();
    sp.set("encounter_type", options.encounterType);
    return `${base}?${sp.toString()}`;
  })();

  const response = await fetchWithAuthRetry(url, { method: "GET" });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch summary template: ${response.status} - ${errorText}`,
    );
  }

  const payload: SummaryTemplateApiResponse = await response.json();
  return mapSummaryTemplateResponse(payload);
};

export const updateSummaryTemplate = async (
  session: TSession,
  payload: {
    templateData: TSummaryTemplateData; // structured-only
    encounterType?: string | null;
    templateName?: string | null;
  },
): Promise<TSummaryTemplateResponse> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to update summary templates.");
  }

  const url = `${urlPrefixDocumentation}/${session.clinicId}/summary-template`;

  const body: Record<string, unknown> = {
    template_data: payload.templateData,
  };
  if (payload.encounterType) body.encounter_type = payload.encounterType;
  if (payload.templateName) body.template_name = payload.templateName;

  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update summary template: ${response.status} - ${errorText}`,
    );
  }

  const responsePayload: SummaryTemplateApiResponse = await response.json();
  return mapSummaryTemplateResponse(responsePayload);
};

export const deleteSummaryTemplate = async (
  session: TSession,
  options: { encounterType?: string; templateId?: number | null } = {},
): Promise<void> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to delete summary templates.");
  }

  const base = `${urlPrefixDocumentation}/${session.clinicId}/summary-template`;
  const u = new URL(base);

  if (options.encounterType)
    u.searchParams.set("encounter_type", options.encounterType);
  if (options.templateId != null)
    u.searchParams.set("template_id", String(options.templateId));

  const response = await fetchWithAuthRetry(u.toString(), { method: "DELETE" });

  if (!response.ok && response.status !== 204) {
    const errorText = await response.text();
    throw new Error(
      `Failed to delete summary template: ${response.status} - ${errorText}`,
    );
  }
};
export const listSummaryTemplateDefaults = async (
  session: TSession,
): Promise<TSummaryTemplateDefinition[]> => {
  if (!session?.clinicId) {
    throw new Error(
      "Clinic session is required to list default summary templates.",
    );
  }

  const url = `${urlPrefixDocumentation}/summary-templates/defaults`;

  const response = await fetchWithAuthRetry(url, { method: "GET" });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to load default summary templates: ${response.status} - ${errorText}`,
    );
  }

  const payload: {
    templates: Array<{
      note_type: string;
      encounter_type: string;
      template: SummaryPromptV1 | string;
      template_json?: { system_prompt?: string; user_prompt?: string };
    }>;
  } = await response.json();

  return payload.templates.map((t) => ({
    noteType: t.note_type,
    encounterType: t.encounter_type,
    template: t.template, // UI can handle both v1 and string
  }));
};

// ------------------------------
// Clinic Note Signatures
// ------------------------------

export type ClinicNoteSignatureResponse = {
  id: number;
  clinic_uuid: string;
  signature: string;
  created_at: string;
  updated_at: string;
};

const getClinicNoteSignatureUrl = (clinicId: string) =>
  `${urlPrefixDocumentation}/${clinicId}/note-signature`;

export const getClinicNoteSignature = async (
  session: TSession,
): Promise<ClinicNoteSignatureResponse | null> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to load note signatures.");
  }

  const url = getClinicNoteSignatureUrl(session.clinicId);
  const response = await fetchWithAuthRetry(url, { method: "GET" });

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch clinic note signature: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export const createClinicNoteSignature = async (
  session: TSession,
  signature: string,
): Promise<ClinicNoteSignatureResponse> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to create note signatures.");
  }

  const url = getClinicNoteSignatureUrl(session.clinicId);
  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify({ signature }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create clinic note signature: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export const updateClinicNoteSignature = async (
  session: TSession,
  signature: string,
): Promise<ClinicNoteSignatureResponse> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to update note signatures.");
  }

  const url = getClinicNoteSignatureUrl(session.clinicId);
  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify({ signature }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update clinic note signature: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export const deleteClinicNoteSignature = async (
  session: TSession,
): Promise<void> => {
  if (!session?.clinicId) {
    throw new Error("Clinic session is required to delete note signatures.");
  }

  const url = getClinicNoteSignatureUrl(session.clinicId);
  const response = await fetchWithAuthRetry(url, { method: "DELETE" });

  if (!response.ok && response.status !== 204) {
    const errorText = await response.text();
    throw new Error(
      `Failed to delete clinic note signature: ${response.status} - ${errorText}`,
    );
  }
};
